import React, { ReactNode, useRef, useState, useCallback, useEffect } from "react";
import { Modal as AntModal, ModalProps as AntModalProps } from "antd";
import classNames from "classnames";
import Button from "@Components/Button";
import "./Modal.scss";
import { MODAL_SIZE } from "@Configs/enum";
import { Close, ArrowLeft } from "assets/icons";

export interface ModalCustomProps extends AntModalProps {
  children?: ReactNode;
  /**Is the modal footer (cancel, apply, apply next button) visible or not*/
  visibleFooter?: boolean;
  /**Change the size of modal*/
  size?: MODAL_SIZE;
  /**Name of apply button*/
  titleButtonApply?: string;
  /**Name of cancel button*/
  titleButtonCancel?: string;
  /**Cancel the form (Button type secondary)*/
  handleCancel?: (event: any) => void;
  /**Function pass when you need to handle a different feature*/
  handleClickIcon?: (event: any) => void;
  /**Save the form (Button type primary)*/
  handleSave?: (event: any) => void;
  /**Pass disable for button cancel */
  disableButtonCancel?: boolean;
  /**Pass disable for button apply */
  disableButtonApply?: boolean;
  isShowButtonCancel?: boolean;
  isShowButtonApply?: boolean;
  isShowIconBack?: boolean;
  /**Enable resizing functionality*/
  resizable?: boolean;
}


interface Size {
  width: number;
  height: number;
}

function Modal(props: ModalCustomProps) {
  const {
    open,
    visibleFooter = true,
    size = MODAL_SIZE.SIZE_1000,
    titleButtonApply,
    titleButtonCancel,
    handleCancel,
    handleSave,
    isShowButtonCancel = true,
    isShowButtonApply = true,
    isShowIconBack = true,
    disableButtonCancel = false,
    disableButtonApply = false,
    handleClickIcon,
    resizable = true,
  } = props;

  const firstLoad = useRef(true);
  const modalRef = useRef<HTMLDivElement>(null);
  const [isResizing, setIsResizing] = useState(false);
  const firstResize = useRef(true);
  const [modalSize, setModalSize] = useState<Size>({
    width: typeof size === 'number' ? size : 1000,
    height: 0
  });
  const [resizeStart, setResizeStart] = useState<{ x: number; y: number; width: number; height: number }>({
    x: 0, y: 0, width: 0, height: 0
  });

  const minWidth = size;
  const maxWidth = 0.9 * window.innerWidth; // 90% of viewport width
  const maxHeight = 0.9 * window.innerHeight; // 90% of viewport height
  const [minHeight, setMinHeight] = useState<number | null>(null);


  // Reset position and size when modal opens/closes
  useEffect(() => {
    if (open && firstLoad.current) {
      setModalSize({
        width: typeof size === 'number' ? size : 1000,
        height: 0
      });
      firstLoad.current = false;
    }
  }, [open, size]);



  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  // Resizing functionality
  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    if (!resizable) return;

    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);

    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: modalSize.width,
      height: modalSize.height
    });
  }, [resizable, modalSize]);

  const handleResizeMouseMove = useCallback((e: MouseEvent) => {
    if (isResizing && resizable) {
      const deltaX = e.clientX - resizeStart.x;
      const deltaY = e.clientY - resizeStart.y;
      const newWidth = Math.max(minWidth, Math.min(maxWidth, resizeStart.width + deltaX));
      const newHeight = Math.max(minHeight, Math.min(maxHeight, resizeStart.height + deltaY));
      setModalSize({
        width: newWidth,
        height: newHeight
      });
    }
  }, [isResizing, resizable, resizeStart, minWidth, maxWidth, minHeight, maxHeight]);

  useEffect(() => {
    if (isResizing) {
      if (firstResize.current) {
        firstResize.current = false;
        const initMinHeight = modalRef.current?.offsetHeight + 64 || 0;
        setMinHeight(initMinHeight);
        setResizeStart({
          ...resizeStart,
          width: minWidth,
          height: initMinHeight
        });
        setModalSize({
          width: typeof size === 'number' ? size : 1000,
          height: initMinHeight || 0
        });
      }
    }
  }, [isResizing, size, minWidth, resizeStart]);



  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleResizeMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, handleResizeMouseMove, handleMouseUp]);



  const renderModalFooter = React.useMemo(
    () => (
      <div className={classNames("footer-modal")}>
        <div
          className={classNames("button-bleed-footer", {
            center: size === MODAL_SIZE.SIZE_400,
          })}
        >
          {isShowButtonCancel && (
            <Button
              type="secondary"
              onClick={handleCancel}
              disabled={disableButtonCancel}
            >
              <span>{titleButtonCancel ? titleButtonCancel : "Cancel"}</span>
            </Button>
          )}
          {isShowButtonApply && (
            <Button
              type="primary"
              disabled={disableButtonApply}
              onClick={handleSave}
            >
              <span>{titleButtonApply ? titleButtonApply : "Apply"}</span>
            </Button>
          )}
        </div>
      </div>
    ),
    [
      size,
      handleCancel,
      disableButtonCancel,
      titleButtonCancel,
      handleSave,
      disableButtonApply,
      titleButtonApply,
      isShowButtonCancel,
      isShowButtonApply,
    ]
  );

  React.useEffect(() => {
    if (open) {
      const handleEscKey = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          handleCancel?.(null);
        }
      };
      document.addEventListener("keydown", handleEscKey);
      return () => {
        document.removeEventListener("keydown", handleEscKey);
      };
    }
  }, [open, handleCancel]);

  const getShowFooter = () => {
    if ((!isShowButtonApply && !isShowButtonCancel) || !visibleFooter) {
      return null;
    }
    return renderModalFooter;
  };

  const modalStyle: React.CSSProperties = {
    // transform: `translate(${modalPosition.x}px, ${modalPosition.y}px)`,
    width: modalSize.width,
    height: resizable && modalSize.height > 0 ? modalSize.height : 'auto',
    maxWidth: 'none',
  };

  return (
    <>
      <AntModal
        {...props}
        centered
        className={classNames("modal__container__normal", props?.className, {
          'modal-resizable': resizable,
        })}
        closeIcon={null}
        title=""
        footer={getShowFooter()}
        onCancel={
          typeof handleClickIcon === "function" ? handleClickIcon : handleCancel
        }
        width={modalSize.width}
        keyboard={false}
        style={modalStyle}
        maskClosable={false}
      >
        <div className="modal_content" ref={modalRef}>
          <div className={classNames("header-modal")} >
            <div className="header-left">
              {isShowIconBack && (
                <div
                  onClick={
                    typeof handleClickIcon === "function"
                      ? handleClickIcon
                      : handleCancel
                  }
                  className="center-icon"
                >
                  <ArrowLeft />
                </div>
              )}
              <div className="title">{props.title}</div>
            </div>
            <div
              onClick={
                typeof handleClickIcon === "function"
                  ? handleClickIcon
                  : handleCancel
              }
              className="center-icon"
            >
              <Close />
            </div>
          </div>
          <div
            className="body-modal"
            style={resizable ? {
              height: modalSize.height - 120, // Subtract header and footer height
              overflowY: 'auto'
            } : {}}
          >
            {props.children}
          </div>
          {resizable && (
            <div
              className="resize-handle resize-stairs"
              onMouseDown={handleResizeMouseDown}
              style={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                width: '20px',
                height: '20px',
                cursor: 'nwse-resize',
                zIndex: 1000,
              }}
            />
          )}
        </div>
      </AntModal>
    </>
  );
}

export default Modal;